import React, {useMemo} from "react";
import useModal, {Modal} from "../hook/useModal";
import exitIcon from "../assets/svgs/exit.svg";
import {useIntl} from "react-intl";
import classNames from "classnames";

type ConfirmDialogProps = {
    dialogId: string;
    title?: string
    content: string;
    confirmVariant?: string;
    confirmText?: string;
    onConfirm: (confirmArgs?: any) => void;
    cancelText?: string;
    onCancel?: (cancelArgs?: any) => void;
    showCancelButton?: boolean;
    closeTriggerOnCancel?: boolean
};

export type ConfirmDialogData = {
    confirmArgs?: any;
    cancelArgs?: any;
}

export const CONFIRM_DIALOG_ID = "confirm-dialog-id";

export const getConfirmDialogId = (additionalDialogId: string) => {
    return `${CONFIRM_DIALOG_ID}-${additionalDialogId}`
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = (props) => {
    const {
        dialogId,
        title,
        content,
        confirmVariant,
        confirmText,
        onConfirm,
        cancelText,
        onCancel,
        showCancelButton = false,
        closeTriggerOnCancel = true
    } = props;

    const intl = useIntl();

    const modal = useModal();

    const modalId = useMemo(() => {
        return getConfirmDialogId(dialogId);
    }, [dialogId])

    const confirmBtnClassName = useMemo(() => {
        const commonClassName = "min-w-[118px]";

        switch (confirmVariant) {
            case "primary":
                return classNames(commonClassName, "console-btn-lg-primary");
            case "danger":
                return classNames(commonClassName, "console-btn-lg-danger");
            default:
                return classNames(commonClassName, "console-btn-lg-gray");

        }
    }, [confirmVariant])

    const onClose = () => {
        const { cancelArgs } = modal.data[modalId] as ConfirmDialogData ?? {};

        closeTriggerOnCancel && onCancel?.(cancelArgs);
    }

    const onClickCancel = () => {
        const { cancelArgs } = modal.data[modalId] as ConfirmDialogData ?? {};

        onCancel?.(cancelArgs);
        modal.close(modalId);
    }

    const onClickConfirm = () => {
        const { confirmArgs } = modal.data[modalId] as ConfirmDialogData ?? {};

        onConfirm?.(confirmArgs);
        modal.close(modalId);
    }

    return (
        <Modal id={modalId}
               size="sm"
               onClose={onClose}
               showCloseButton={true}
               customCloseButton={(
                   <img src={exitIcon}
                        alt="exit"
                        className="absolute right-[10px] top-[10px] cursor-pointer"
                   />
               )}
               className="!p-0 !rounded-[8px] !shadow-[0px_4px_8px_0px_rgba(16, 24, 64, 0.08)]"
        >
            <div className="relative w-full flex flex-col items-center font-pretendard">
                {title &&
                    <div className="absolute font-medium text-[18px] left-[20px] top-[10px]">
                        {title}
                    </div>
                }

                <p className="whitespace-pre-wrap w-full mt-[44px] px-[24px] py-[16px] text-center font-normal text-[16px] leading-[200%] text-[#37352F]">
                    {content}
                </p>
                <div className="whitespace-nowrap flex items-center w-full justify-center space-x-[4px] px-[20px] pt-[16px] pb-[32px]">
                    {showCancelButton &&
                        <button tabIndex={-1} className="console-btn-lg-gray min-w-[118px]" onClick={onClickCancel}>
                            {cancelText ?? intl.formatMessage({id: "b0001"})}
                        </button>
                    }
                    <button tabIndex={-1} className={confirmBtnClassName} onClick={onClickConfirm}>
                        {confirmText ?? intl.formatMessage({id: "b0002"})}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default ConfirmDialog;
